import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Coins, Play, Timer, MagnifyingGlass, Check } from '@phosphor-icons/react';

const SCENES = [
    {
        id: 1, name: "Coral Reef",
        base: [
            { emoji: "🪸", x: 10, y: 70 }, { emoji: "🐠", x: 30, y: 40 }, { emoji: "🐢", x: 60, y: 30 },
            { emoji: "🐙", x: 80, y: 60 }, { emoji: "🌊", x: 50, y: 10 }, { emoji: "⭐", x: 20, y: 80 },
            { emoji: "🐚", x: 70, y: 85 }, { emoji: "🪼", x: 45, y: 55 }, { emoji: "🐡", x: 15, y: 45 },
        ],
        differences: [
            { idx: 1, original: "🐠", changed: "🐟", x: 30, y: 40 },
            { idx: 3, original: "🐙", changed: "🦑", x: 80, y: 60 },
            { idx: 5, original: "⭐", changed: "🌟", x: 20, y: 80 },
            { idx: 7, original: "🪼", changed: "🫧", x: 45, y: 55 },
            { idx: 8, original: "🐡", changed: "🐳", x: 15, y: 45 },
        ]
    },
    {
        id: 2, name: "Outback Sunset",
        base: [
            { emoji: "🦘", x: 25, y: 50 }, { emoji: "🌵", x: 10, y: 65 }, { emoji: "☀️", x: 80, y: 15 },
            { emoji: "🏜️", x: 50, y: 75 }, { emoji: "🦎", x: 65, y: 55 }, { emoji: "🐍", x: 40, y: 40 },
            { emoji: "🕷️", x: 85, y: 70 }, { emoji: "🦅", x: 35, y: 15 }, { emoji: "🌾", x: 70, y: 80 },
        ],
        differences: [
            { idx: 0, original: "🦘", changed: "🐕", x: 25, y: 50 },
            { idx: 2, original: "☀️", changed: "🌙", x: 80, y: 15 },
            { idx: 4, original: "🦎", changed: "🐸", x: 65, y: 55 },
            { idx: 6, original: "🕷️", changed: "🐜", x: 85, y: 70 },
            { idx: 7, original: "🦅", changed: "🦜", x: 35, y: 15 },
        ]
    },
    {
        id: 3, name: "Rainforest",
        base: [
            { emoji: "🌿", x: 10, y: 30 }, { emoji: "🐨", x: 30, y: 25 }, { emoji: "🦋", x: 55, y: 20 },
            { emoji: "🐸", x: 75, y: 50 }, { emoji: "🌺", x: 20, y: 60 }, { emoji: "🐦", x: 60, y: 40 },
            { emoji: "🍃", x: 85, y: 30 }, { emoji: "🦜", x: 45, y: 70 }, { emoji: "🍄", x: 40, y: 85 },
        ],
        differences: [
            { idx: 1, original: "🐨", changed: "🐻", x: 30, y: 25 },
            { idx: 2, original: "🦋", changed: "🐝", x: 55, y: 20 },
            { idx: 4, original: "🌺", changed: "🌸", x: 20, y: 60 },
            { idx: 5, original: "🐦", changed: "🐦‍⬛", x: 60, y: 40 },
            { idx: 8, original: "🍄", changed: "🌱", x: 40, y: 85 },
        ]
    }
];

function shuffle(arr) { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }

export default function SpotDifferenceGame() {
    const { api } = useAuth();
    const [started, setStarted] = useState(false);
    const [scenes, setScenes] = useState([]);
    const [currentScene, setCurrentScene] = useState(0);
    const [found, setFound] = useState([]);
    const [time, setTime] = useState(0);
    const [totalFound, setTotalFound] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [result, setResult] = useState(null);
    const timerRef = useRef(null);

    const startGame = () => {
        setScenes(shuffle(SCENES).slice(0, 2));
        setCurrentScene(0); setFound([]); setTime(0); setTotalFound(0);
        setGameOver(false); setResult(null); setStarted(true);
        timerRef.current = setInterval(() => setTime(t => t + 1), 1000);
    };

    useEffect(() => { return () => { if (timerRef.current) clearInterval(timerRef.current); }; }, []);

    const handleClick = (diffIdx) => {
        if (found.includes(diffIdx)) return;
        const newFound = [...found, diffIdx];
        setFound(newFound);
        setTotalFound(t => t + 1);

        if (newFound.length === scenes[currentScene].differences.length) {
            // All found in this scene
            setTimeout(() => {
                if (currentScene + 1 >= scenes.length) {
                    clearInterval(timerRef.current); setGameOver(true); completeGame(totalFound + 1);
                } else {
                    setCurrentScene(c => c + 1); setFound([]);
                }
            }, 800);
        }
    };

    const handleSkipScene = () => {
        if (currentScene + 1 >= scenes.length) {
            clearInterval(timerRef.current); setGameOver(true); completeGame(totalFound);
        } else {
            setCurrentScene(c => c + 1); setFound([]);
        }
    };

    const completeGame = async (total) => {
        const maxDiffs = scenes.reduce((s, sc) => s + sc.differences.length, 0);
        try {
            const res = await api().post('/games/complete', {
                game_id: 'spot_difference', score: Math.round((total / maxDiffs) * 100), time_seconds: time, difficulty: 'medium'
            });
            setResult(res.data);
        } catch (err) { console.error('Failed to submit'); }
    };

    const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    if (!started) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-20">
                            <Link to="/games" className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-[#ec4899] border-4 border-[#1e293b] flex items-center justify-center">
                                    <MagnifyingGlass weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b]">Spot the Difference</span>
                            </Link>
                            <Link to="/games" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5"><ArrowLeft weight="bold" size={16} /> Games</Link>
                        </div>
                    </div>
                </nav>
                <div className="max-w-2xl mx-auto px-4 pt-28 pb-24 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-6xl mb-4">🔍</div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b] mb-2">Spot the Difference</h1>
                        <p className="text-[#475569] mb-8">Compare two Australian nature scenes and find 5 differences in each. Click on the differences in the right scene!</p>
                        <motion.button onClick={startGame} whileHover={{ y: -4, x: -4 }} whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn bg-[#ec4899] text-white text-lg flex items-center gap-2 mx-auto" data-testid="start-game">
                            <Play weight="bold" size={22} /> Start
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (gameOver) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="brutalist-card p-8 max-w-md w-full text-center bg-white" data-testid="game-complete">
                    <div className="text-6xl mb-4">🎉</div>
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Sharp Eyes!</h2>
                    <p className="text-[#475569] mb-6">{totalFound} differences found in {formatTime(time)}</p>
                    <div className="p-3 bg-[#fef3c7] rounded-xl mb-6 inline-block">
                        <Coins weight="duotone" size={20} className="text-[#f59e0b] mx-auto" />
                        <p className="font-fredoka text-xl font-bold">{result ? `+${result.tokens_earned}` : '...'} Tokens</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={startGame} className="brutalist-btn flex-1 bg-[#ec4899] text-white"><Play weight="bold" size={18} /> Again</button>
                        <Link to="/games" className="brutalist-btn flex-1 bg-white text-[#1e293b] text-center">Games Hub</Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    const scene = scenes[currentScene];
    const modified = scene.base.map((item, i) => {
        const diff = scene.differences.find(d => d.idx === i);
        return diff ? { ...item, emoji: diff.changed, diffIdx: scene.differences.indexOf(diff) } : { ...item, diffIdx: -1 };
    });

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)]">
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b] px-4 py-3">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Link to="/games" className="text-[#475569] font-semibold flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Games</Link>
                    <span className="font-fredoka font-bold text-[#1e293b]">{scene.name} — Scene {currentScene + 1}/{scenes.length}</span>
                    <div className="flex gap-3 text-sm font-semibold">
                        <span className="text-[#ec4899]">{found.length}/5 found</span>
                        <span className="text-[#a855f7]">{formatTime(time)}</span>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 pt-16 pb-8">
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {/* Original */}
                    <div className="brutalist-card p-4 bg-white">
                        <p className="font-fredoka font-bold text-[#1e293b] mb-2 text-center">Original</p>
                        <div className="relative bg-[#e0f2fe] rounded-xl" style={{ height: 320 }}>
                            {scene.base.map((item, i) => (
                                <span key={i} className="absolute text-3xl" style={{ left: `${item.x}%`, top: `${item.y}%`, transform: 'translate(-50%,-50%)' }}>
                                    {item.emoji}
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* Modified */}
                    <div className="brutalist-card p-4 bg-white">
                        <p className="font-fredoka font-bold text-[#1e293b] mb-2 text-center">Find the Differences!</p>
                        <div className="relative bg-[#e0f2fe] rounded-xl cursor-crosshair" style={{ height: 320 }}>
                            {modified.map((item, i) => {
                                const isFound = item.diffIdx >= 0 && found.includes(item.diffIdx);
                                const isDiff = item.diffIdx >= 0;
                                return (
                                    <span key={i}
                                        onClick={() => isDiff && handleClick(item.diffIdx)}
                                        className={`absolute text-3xl transition-all ${isDiff ? 'cursor-pointer hover:scale-125' : ''} ${isFound ? 'ring-4 ring-[#22c55e] rounded-full bg-[#22c55e]/20' : ''}`}
                                        style={{ left: `${item.x}%`, top: `${item.y}%`, transform: 'translate(-50%,-50%)' }}
                                        data-testid={isDiff ? `diff-${item.diffIdx}` : undefined}>
                                        {item.emoji}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="text-center mt-4">
                    <button onClick={handleSkipScene} className="text-sm text-[#475569] hover:text-[#1e293b] font-semibold">
                        Skip this scene →
                    </button>
                </div>
            </div>
        </div>
    );
}
